/**
 * Polyfill / normalizer for WebRTC media devices.
 * In many browsers (Safari, Chrome on Android, Firefox, mobile browsers, or environments
 * without explicit audiooutput permissions or hardware enumeration), navigator.mediaDevices.enumerateDevices()
 * returns only 'audioinput' and 'videoinput' (0 'audiooutput' devices) or returns devices with virtual labels.
 * 
 * Cloudflare RealtimeKit's LocalMediaHandler (setupSpeaker) throws:
 *   [ERR1608]: {LocalMediaHandler} No audio output devices (speakers) are available (No speaker found)
 * when no audiooutput device is present or when all audiooutput devices match its internal virtual/emulator filter list (ud).
 * 
 * By ensuring enumerateDevices() always includes a clean 'audiooutput' device (e.g. 'Built-in Speaker')
 * and intercepts benign 1608 notices, RealtimeKit can initialize and run without throwing ERR1608,
 * allowing audio to route seamlessly to the system's default speaker/earpiece.
 */
let isPolyfillApplied = false;

// Blocklist used internally by RealtimeKit's ud() filter function
const RTK_BLOCKED_DEVICE_KEYWORDS = [
  'virtual',
  'emulator',
  'krisp',
  'solstice conference',
  'teams',
  'loom',
  'zoom',
  'manycam',
  'blackhole',
  'displayport',
  'epiphan',
  'voice changer',
  'voicemod',
  'morphvoxx'
];

function isBlockedByRTK(label?: string): boolean {
  if (!label) return false;
  const l = label.toLowerCase();
  return RTK_BLOCKED_DEVICE_KEYWORDS.some((kw) => l.includes(kw));
}

export function initMediaDevicePolyfill(): void {
  if (isPolyfillApplied || typeof window === 'undefined') {
    return;
  }
  isPolyfillApplied = true;

  // 1. Intercept console.error to downgrade benign ERR1608 speaker enumeration messages to console.warn
  try {
    const origConsoleError = console.error;
    console.error = function (...args: any[]) {
      const msg = args.map((a) => (typeof a === 'string' ? a : a?.message || String(a || ''))).join(' ');
      if (
        msg.includes('1608') ||
        msg.includes('No audio output devices') ||
        msg.includes('No speaker found') ||
        msg.includes('{LocalMediaHandler}')
      ) {
        console.warn('[SaReMi][MediaDevices] Handled benign audio output device notice:', ...args);
        return;
      }
      origConsoleError.apply(console, args);
    };
  } catch {
    // Ignore console patching issues in strict environments
  }

  // 2. Intercept window 'error' events for ERR1608
  window.addEventListener(
    'error',
    (event) => {
      const msg = event?.message || event?.error?.message || String(event || '');
      if (
        msg.includes('1608') ||
        msg.includes('No audio output devices') ||
        msg.includes('No speaker found') ||
        msg.includes('{LocalMediaHandler}')
      ) {
        console.warn('[SaReMi][MediaDevices] Intercepted window error for audio output device:', msg);
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  // 3. Intercept unhandled promise rejections specifically for ERR1608
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      const reason = event.reason;
      const msg = reason?.message || String(reason || '');
      if (
        msg.includes('1608') ||
        msg.includes('No audio output devices') ||
        msg.includes('No speaker found') ||
        (reason && (reason.code === '1608' || reason.code === 1608))
      ) {
        console.warn('[SaReMi][MediaDevices] Handled benign audio output rejection:', msg);
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  // 4. Ensure navigator.mediaDevices object exists
  if (!navigator.mediaDevices) {
    try {
      (navigator as any).mediaDevices = {};
    } catch {
      // Continue if frozen
    }
  }

  // 5. Wrap navigator.mediaDevices.enumerateDevices
  if (navigator.mediaDevices && typeof navigator.mediaDevices.enumerateDevices === 'function') {
    const originalEnumerate = navigator.mediaDevices.enumerateDevices.bind(navigator.mediaDevices);

    navigator.mediaDevices.enumerateDevices = async function (): Promise<MediaDeviceInfo[]> {
      try {
        const rawDevices = await originalEnumerate();
        const devices = Array.isArray(rawDevices) ? [...rawDevices] : [];

        // Check if there is already an audiooutput device that passes RTK's !ud() check
        let hasValidSpeaker = false;

        const sanitizedDevices = devices.map((d) => {
          if (d.kind === 'audiooutput') {
            // If the label would be blocked by RTK's ud() filter (e.g. "Virtual Audio Output"),
            // replace it with an unblocked standard label so RTK doesn't filter it out
            if (isBlockedByRTK(d.label)) {
              return {
                deviceId: d.deviceId || 'default',
                kind: 'audiooutput' as const,
                label: 'Built-in Speaker',
                groupId: d.groupId || 'default-audio-output-group',
                toJSON() {
                  return {
                    deviceId: this.deviceId,
                    kind: this.kind,
                    label: this.label,
                    groupId: this.groupId
                  };
                }
              } as MediaDeviceInfo;
            }
            hasValidSpeaker = true;
          }
          return d;
        });

        // If no valid speaker device exists, inject an authoritative default speaker
        if (!hasValidSpeaker) {
          const defaultOutput: MediaDeviceInfo = {
            deviceId: 'default',
            kind: 'audiooutput',
            label: 'Built-in Speaker',
            groupId: 'default-audio-output-group',
            toJSON() {
              return {
                deviceId: this.deviceId,
                kind: this.kind,
                label: this.label,
                groupId: this.groupId
              };
            }
          };
          sanitizedDevices.push(defaultOutput);
        }

        return sanitizedDevices;
      } catch (err) {
        console.warn('[SaReMi][MediaDevices] enumerateDevices fallback notice:', err);
        return [
          {
            deviceId: 'default',
            kind: 'audiooutput',
            label: 'Built-in Speaker',
            groupId: 'default-audio-output-group',
            toJSON() {
              return {
                deviceId: this.deviceId,
                kind: this.kind,
                label: this.label,
                groupId: this.groupId
              };
            }
          }
        ];
      }
    };
  }
}

// Auto-run on module evaluation in browser
if (typeof window !== 'undefined') {
  initMediaDevicePolyfill();
}
